import { gen2Request } from 'src/services/gen_2_http_reqs';
import type { Device } from 'src/types/Device';

type Rect = [number, number, number, number];

export async function startRadarRequest(device: Device): Promise<unknown | null> {
	try {
		const radar = await gen2Request(device, 'Presence.LiveTrack', {
			enable: true,
			interval: 1000,
		});
		return radar.isok ? radar.data : null;
	} catch (error) {
		console.error('Radar request failed:', error);
		return null;
	}
}

export async function addZoneRequest(
	device: Device,
	name: string = 'Zone',
	enabled: boolean = true,
	zmin: number = 0,
	zmax: number = 0,
	color: number[] = [],
	area: Rect[] = [],
): Promise<{ isok: boolean; id?: number } | null> {
	try {
		const config = { name, enabled, zmin, zmax, color, area };
		const zone = await gen2Request(device, 'presence.addzone', { config });
		return zone.isok ? { isok: true, ...(zone.data ?? {}) } : null;
	} catch (error) {
		console.error('Add zone request failed:', error);
		return null;
	}
}

export async function updateZoneRequest(
	id: number,
	device: Device,
	name: string = 'Zone',
	enabled: boolean = true,
	zmin: number = 0,
	zmax: number = 0,
	color: number[] = [],
	area: Rect[] = [],
): Promise<{ isok: boolean } | null> {
	try {
		const config = { name, enabled, zmin, zmax, color, area };
		const zone = await gen2Request(device, 'presencezone.setconfig', { id, config });
		return zone.isok ? { isok: true } : null;
	} catch (error) {
		console.error('Update zone request failed:', error);
		return null;
	}
}

export async function deleteZoneRequest(id: number, device: Device): Promise<{ isok: boolean }> {
	try {
		return await gen2Request(device, 'presence.deletezone', { id });
	} catch (error) {
		console.error('Delete zone request failed:', error);
		return { isok: false };
	}
}

export async function setBlindZonesAndRoomBoundaries(
	device: Device,
	blind: Rect[] = [],
	room: Rect[] = [],
	mainZoneId?: number,
): Promise<{ isok: boolean; error?: unknown }> {
	try {
		const tasks: Promise<unknown>[] = [setBlindSpotsRequest(device, blind)];
		if (mainZoneId !== undefined && room.length > 0) {
			tasks.push(updateMainZoneAreaRequest(device, mainZoneId, room));
		}
		await Promise.all(tasks);
		return { isok: true };
	} catch (error) {
		console.error('Room update request failed:', error);
		return { isok: false, error };
	}
}

export async function setBlindSpotsRequest(device: Device, blind: Rect[] = []): Promise<unknown | null> {
	try {
		const request = await gen2Request(device, 'presence.setconfig', {
			config: { blind },
		});
		return request.isok ? request.data : null;
	} catch (error) {
		console.error('Blind spots update request failed:', error);
		return null;
	}
}

export async function updateMainZoneAreaRequest(
	device: Device,
	zoneId: number,
	area: Rect[] = [],
): Promise<unknown | null> {
	try {
		const currentZone = await gen2Request(device, 'presencezone.getconfig', { id: zoneId });
		if (!currentZone.isok) return null;

		const config = { ...currentZone.data, area };
		const request = await gen2Request(device, 'presencezone.setconfig', {
			id: zoneId,
			config,
		});
		return request.isok ? request.data : null;
	} catch (error) {
		console.error('Main zone area update request failed:', error);
		return null;
	}
}

export async function updateZoneConfigRequest(
	device: Device,
	zoneId: number,
	config: Record<string, unknown>,
): Promise<{ isok: boolean }> {
	try {
		return await gen2Request(device, 'presencezone.setconfig', { id: zoneId, config });
	} catch (error) {
		console.error('Zone config update failed:', error);
		return { isok: false };
	}
}

interface PresenceMainConfigParams {
	enable?: boolean;
	zmin?: number;
	zmax?: number;
	sensor?: {
		height?: number;
		flipped?: boolean;
		power?: string;
		sensitivity?: string;
	};
	ui?: {
		imperial?: boolean;
	};
}

export async function setPresenceMainConfig(
	device: Device,
	config: PresenceMainConfigParams,
): Promise<{ isok: boolean } | null> {
	try {
		const request = await gen2Request(device, 'presence.setconfig', { config });
		return request.isok ? request : null;
	} catch (error) {
		console.error('Presence main config request failed:', error);
		return null;
	}
}

interface FineTuningConfigParams {
	sensor?: {
		points?: number;
		velocity?: number;
		snr?: number;
		max_velocity?: number;
		state?: {
			det_act_thr?: number;
			det_free_thr?: number;
			act_free_thr?: number;
			stat_free_thr?: number;
			sleep_free_thr?: number;
		};
	};
}

export async function setFineTuningConfig(
	device: Device,
	config: FineTuningConfigParams,
): Promise<{ isok: boolean } | null> {
	try {
		const request = await gen2Request(device, 'presence.setconfig', { config });
		return request.isok ? request : null;
	} catch (error) {
		console.error('Fine-tuning config request failed:', error);
		return null;
	}
}

interface LedConfigParams {
	leds?: {
		brightness?: number;
		night_mode?: {
			enable?: boolean;
			brightness?: number;
			active_between?: string[];
		};
	};
}

export async function setLedConfig(
	device: Device,
	config: LedConfigParams,
): Promise<{ isok: boolean } | null> {
	try {
		const request = await gen2Request(device, 'presence.setconfig', { config });
		return request.isok ? request : null;
	} catch (error) {
		console.error('LED config request failed:', error);
		return null;
	}
}

export async function requestPresenceTiltCalibrate(device: Device): Promise<{ isok: boolean } | null> {
	try {
		// Local RPC may accept params:null; cloud Jrpc often validates params as an object — use {}.
		const request = await gen2Request(device, 'Presence.TiltCalibrate', {});
		return request.isok ? request : null;
	} catch (error) {
		console.error('Presence.TiltCalibrate request failed:', error);
		return null;
	}
}
